import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { rupee } from "@/lib/constants";
import { Loader2, RefreshCw } from "lucide-react";
import { FilterBar, inRange, uniq } from "@/components/admin/FilterBar";
import { fmtTs } from "@/components/Timeline";

// Booking-wise ledger of every rupee received (visit fee + service + material cash), auto-synced with Cashfree.
export function PaymentsLedger() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [method, setMethod] = useState("");
    const [due, setDue] = useState("");
    const [dates, setDates] = useState({ from: "", to: "" });
    const load = () => { setRows(null); api.get("/admin/payments-ledger").then((r) => setRows(r.data)).catch(() => setRows([])); };
    useEffect(() => { load(); }, []);
    const shown = (rows || []).filter((r) => (!method || r.method === method) && inRange(r.updated_at, dates)
        && (!due || (due === "due" ? r.due > 0 : r.due <= 0))
        && (!q || `${r.booking_ref} ${r.customer_name} ${r.service_name}`.toLowerCase().includes(q.toLowerCase())));
    const tot = (k) => shown.reduce((a, r) => a + (r[k] || 0), 0);
    return (
        <div className="mt-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["received", "Received"], ["due", "Due"], ["material_cash", "Material advance"], ["refunded", "Refunds"]].map(([k, l]) => (
                    <div key={k} className="rounded-2xl border border-navy/10 bg-white p-3" data-testid={`ledger-total-${k}`}><p className="text-[11px] font-bold uppercase tracking-wide text-navy/45">{l}</p><p className="font-heading text-lg font-extrabold text-navy">{rupee(tot(k))}</p></div>
                ))}
            </div>
            <div className="flex items-end gap-2">
                <div className="flex-1"><FilterBar testid="ledger-filter" q={q} setQ={setQ} placeholder="Search booking ID, customer, service…" dates={dates} setDates={setDates}
                    selects={[{ key: "method", label: "All methods", value: method, set: setMethod, options: uniq((rows || []).map((r) => r.method)) },
                        { key: "due", label: "Due & settled", value: due, set: setDue, options: [{ value: "due", label: "Has due" }, { value: "settled", label: "Settled" }] }]}
                    onClear={() => { setQ(""); setMethod(""); setDue(""); setDates({ from: "", to: "" }); }} /></div>
                <button data-testid="ledger-refresh-btn" onClick={load} className="flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white"><RefreshCw size={13} /> Sync</button>
            </div>
            <div className="mt-3 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                <div className="overflow-x-auto homiie-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                            <tr>{["Booking", "Customer", "Bill", "Visit fee", "Service paid", "Material cash", "Received", "Due", "Refunds", "Method / status", "Updated"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {shown.map((r) => (
                                <tr key={r.booking_id} className="border-t border-navy/5" data-testid={`ledger-row-${r.booking_ref}`}>
                                    <td className="px-3 py-2.5"><Link to={`/admin/bookings/${r.booking_id}`} className="font-heading font-bold text-navy hover:text-orange">{r.booking_ref}</Link><div className="text-[11px] text-navy/45">{r.service_name}</div></td>
                                    <td className="px-3 py-2.5 text-navy/70">{r.customer_name}</td>
                                    <td className="px-3 py-2.5">{rupee(r.bill_total)}</td>
                                    <td className="px-3 py-2.5">{rupee(r.visit_fee_paid)}</td>
                                    <td className="px-3 py-2.5">{rupee(r.service_paid)}</td>
                                    <td className="px-3 py-2.5">{rupee(r.material_cash)}</td>
                                    <td className="px-3 py-2.5 font-bold text-emerald-700">{rupee(r.received)}</td>
                                    <td className={`px-3 py-2.5 font-bold ${r.due > 0 ? "text-rose-600" : "text-navy/40"}`}>{rupee(r.due)}</td>
                                    <td className="px-3 py-2.5">{r.refunded ? rupee(r.refunded) : "—"}</td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-navy/60">{r.method || "—"} · {r.payment_status}</td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-navy/45">{fmtTs(r.updated_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows && shown.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No payments match.</div>}
                </div>
            </div>
        </div>
    );
}
